import { useRegistration } from "@/context/RegistrationContext";
import { POLICY_ITEMS } from "@/lib/constants";
import { ArrowLeft, ArrowRight, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { PolicyBuilder } from "@/components/PolicyBuilder";

export default function PoliciesStep({ onNext, onBack }) {
  const { state, setState } = useRegistration();
  const policies = state.policies || {};
  const policiesFilled = state.policiesFilled || {};

  // Merge file metadata from `policies` with candidate-typed values from `policiesFilled`
  // so PolicyBuilder can consume a single `value` prop as before.
  const valueFor = (id) => ({ ...(policies[id] || {}), filled: policiesFilled[id] || {} });

  const setOne = (id, val) => {
    // Split incoming value: file-related bits stay on `policies`, the `.filled`
    // form values go to `policiesFilled` (persisted separately on the backend).
    const { filled, ...fileMeta } = val || {};
    setState((s) => ({
      ...s,
      policies: { ...(s.policies || {}), [id]: fileMeta },
      policiesFilled: { ...(s.policiesFilled || {}), [id]: filled || {} },
    }));
  };

  // Policies marked `optional` don't have a Leegality workflow configured yet
  // — don't block wizard progress on them.
  const requiredItems = POLICY_ITEMS.filter((p) => !p.optional);
  const sentCount = requiredItems.filter((p) => !!policies[p.id]?.sent).length;
  const allDone = sentCount === requiredItems.length;

  const proceed = () => {
    if (!allDone) {
      const missing = requiredItems.filter((p) => !policies[p.id]?.sent).map((p) => p.label);
      toast.error(`All forms & policies are mandatory — pending: ${missing.length}`);
      return;
    }
    onNext();
  };

  return (
    <div className="space-y-6">
      <div className="mu-card p-5 bg-[#FBE7EA]/40 border-[#FBE7EA]">
        <div className="text-sm text-[#111]">
          <span className="font-semibold">All forms & policies below are mandatory.</span> Read the policy and check the acknowledgement, then fill and sign the remaining forms. You can continue once every item is complete — signatures can still finish completing in the background.
          <div className="mt-2 h-2 rounded-full bg-white overflow-hidden">
            <div className="h-full bg-[#B60F2A] transition-all" style={{ width: `${(sentCount / requiredItems.length) * 100}%` }} />
          </div>
          <div className="mt-2 text-xs text-[#6B7280]" data-testid="policies-progress">
            {sentCount} of {requiredItems.length} completed
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5">
        {POLICY_ITEMS.map((p) => (
          <PolicyBuilder key={p.id} item={p} value={valueFor(p.id)} onChange={(v) => setOne(p.id, v)} state={state} />
        ))}
      </div>

      <div className="flex justify-between pt-2">
        <button onClick={onBack} className="mu-btn-secondary" data-testid="btn-back-policies">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <button onClick={proceed} className="mu-btn-primary disabled:opacity-50" data-testid="btn-next-policies" disabled={!allDone}>
          Continue <ArrowRight className="h-4 w-4" />
        </button>
      </div>
      {!allDone && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 inline-flex items-center gap-2">
          <AlertCircle className="h-3.5 w-3.5" />
          You cannot continue until every form & policy has been sent for digital signature.
        </div>
      )}
    </div>
  );
}
